import {useEffect, useMemo, useRef, useState} from "react";
import {useIntl} from "react-intl";
import {useLocation, useParams} from "react-router-dom";
import {TKey} from "@anton.bobrov/react-components";
import {notify, TFormSelectOption} from "@housebets/housebets-x-package";

import bridgeApi from "app/api/bridgeApi";
import apiCasino from "app/api/casino";
import apiGame from "app/api/game";
import {useAppSelector} from "hooks/useAppSelector";
import {setLoginType} from "app/store/slices/setting";
import {LoginType} from "app/types/common";
import {GameInfo} from "app/types/game";
import {gameHandler, getCurrencyIcon} from "app/utils/help";
import {useWindowSize} from "app/utils/hooks/useWindowSize";
import {useToggle} from "hooks/useToggle";
import {useFullscreen} from "hooks/useFullscreen";
import {config} from "app/config";
import {useDispatch} from "react-redux";

const convertCurrencyProvider = [2, 3, 10, 12, 13, 14, 15, 18];

export const useGameData = () => {
    const {$t} = useIntl();
    const param = useParams();
    const dispatch = useDispatch();
    const gameInfo = param.gameInfo;
    const providerName = gameInfo?.split("-")[0];
    const gameId = gameInfo?.split("-")[gameInfo.split("-").length - 1] || 0;
    const {pathname} = useLocation();
    const iframeRef = useRef<HTMLDivElement>(null);
    console.log('gameInfo', gameInfo);
    const isWidgetGame = () => {   
        return gameInfo?.includes('dice-housegames')
    }
    const [show, toggle] = useToggle(false);
    useFullscreen(iframeRef, show, {onClose: () => toggle(false)});

    const convertedToken = useAppSelector((state) => state.config.platformConfig?.convertedToken) || [];
    const userCurrency = useAppSelector((state) => state.user.userCurrency);
    const isLogin = useAppSelector((state) => state.user.isLogin);
    const token = useAppSelector((state) => state.user.token);
    const isUserPlayRealGame = useAppSelector((state) => state.game.isUserPlayRealGame);
    const locale = useAppSelector((state) => state.config.locale);

    const [hasGetGameUrl, setHasGetGameUrl] = useState(false);
    const [isLoadingGame, setIsLoadingGame] = useState(true);
    const [showGameBanner, setShowGameBanner] = useState(false);
    const [isCollect, setIsCollect] = useState(false);

    const CURRENCY_OPTIONS = useMemo(() => {
        const res: TFormSelectOption[] = [];
        convertedToken.forEach((v) => {
            const temp = {
                id: v.tokenId,
                name: v.tokenName,
                icon: getCurrencyIcon(v.tokenId),
            };
            res.push(temp);
        });
        return res;
    }, []);

    const {isMobile, isTablet} = useWindowSize();

    const [gameData, setGameData] = useState<GameInfo>({gameId: 0, gameInlineType: 0, gameName: "", providerName: "", providerId: 0, langId: 0});
    const [relatedGames, setRelatedGames] = useState<GameInfo[]>([]);
    const [isDemoMode, setIsDemoMode] = useState(false);
    const [gameUrl, setGameUrl] = useState("");

    const [selectedId, setSelectedId] = useState(0);

    const gameIframeRef = useRef<HTMLIFrameElement | null>(null);

    useEffect(() => {
        const receivedMessage = (event: MessageEvent) => {
            const message = event.data;

            if (message?.type === "iframe-height") {
                const minimumHeight = 600;
                const finalheight = Math.max(message.height, minimumHeight);
                const iframeElement = document.getElementById("game-container");
                if (iframeElement) {
                    iframeElement.style.height = `${finalheight}px`;
                }
                return;
            }

            if (message?.type === "request-auth-token") {
                sendAuthTokenToIframe();
                return;
            }

            try {
                const msgData = JSON.parse(message);
                if (!msgData?.action) return;
                switch (msgData.action) {
                    case "return-game":
                        setGameUrl("");
                        break;
                }
            } catch {
                // empty
            }
        };
        window.addEventListener("message", receivedMessage);
        return () => {
            window.removeEventListener("message", receivedMessage);
        };
    }, [token]);

    const sendAuthTokenToIframe = () => {
        if (!gameIframeRef.current || !token) return;

        // Ensure we only send the token to our own iframe
        const targetOrigin = new URL(gameUrl).origin;
        const message = {
            type: "auth-token",
            token: token,
        };

        try {
            gameIframeRef.current.contentWindow?.postMessage(JSON.stringify(message), targetOrigin);
            console.log("Auth token sent to game iframe");
        } catch (error) {
            console.error("Failed to send auth token to iframe:", error);
        }
    };

    const onSelectChange = (ids: TKey[]) => {
        setSelectedId(Number(ids[0]));
    };

    const playHandler = async (isDemo: boolean) => {
        if (!isLogin) {
            dispatch(setLoginType(LoginType.LOGIN));
            return;
        }
        setIsLoadingGame(true);
        try {
            const res = await bridgeApi.getGameUrl({
                token: token,
                currency: userCurrency,
                raw_currency: convertCurrencyProvider.includes(gameData.providerId || 0) ? selectedId : userCurrency,
                lang: locale,
                demo: isDemo ? 1 : 0,
                game_id: gameData.gameId,
                platformId: config.platform_id,
            });

            if (res.status === 200 && res.data.code === 0) {
                if (isMobile) {
                    window.location.href = res.data.url;
                    return;
                }
                setGameUrl(res.data.url);
            } else {
                if (res.status === 200 && res.data.code === 2001) {
                    dispatch(setLoginType(LoginType.LOGIN));
                } else {
                    setGameUrl("");
                }
                notify({
                    type: "error",
                    title: res.data.code ? $t({id: `GAME_ERROR_${res.data.code}`}) : res.data.message || "",
                });
                setIsLoadingGame(false);
            }
        } catch (err) {
            const msg = err as any;
            notify({
                type: "error",
                title: msg?.message || "Some error",
            });
        } finally {
            setHasGetGameUrl(true);
            setIsLoadingGame(false);
        }
    };

    const onMaxClick = () => {
        const gameDom = document.getElementById("gameContainer") as HTMLIFrameElement;
        if (!gameDom) return;
        toggle();
    };

    useEffect(() => {
        if (CURRENCY_OPTIONS && CURRENCY_OPTIONS.find((item) => item.id === 10)) {
            setSelectedId(Number(CURRENCY_OPTIONS.find((item) => item.id === 10)?.id ?? 0));
        } else if (CURRENCY_OPTIONS && CURRENCY_OPTIONS.find((item) => item.id === 1)) {
            setSelectedId(Number(CURRENCY_OPTIONS.find((item) => item.id === 1)?.id ?? 0));
        } else {
            setSelectedId(Number(CURRENCY_OPTIONS[0].id));
        }
    }, [CURRENCY_OPTIONS]);

    useEffect(() => {
        const getGameById = async () => {
            const res = await apiGame.getGameById(Number(gameId));
            if (res.status === 200 && res.data?.code === 0) {
                setGameData(gameHandler(res.data?.data));
                return;
            }
        };

        getGameById();
    }, [gameId]);

    useEffect(() => {
        const providerId = gameData.providerId || 0;
        if (providerId) {
            if (gameData.gameId !== 0 && !convertCurrencyProvider.includes(providerId)) {
                playHandler(false);
            }
            if (convertCurrencyProvider.includes(providerId)) {
                setShowGameBanner(true);
            }
            setIsLoadingGame(false);
        }
    }, [providerName, gameData]);

    useEffect(() => {
        const getRelatedGames = async () => {
            const res = await apiCasino.getRelatedGameList(Number(gameData?.providerId));
            if (res.status === 200 && res.data.code === 0) {
                const list = res.data.data.gameList || [];
                list.forEach((v: GameInfo) => {
                    v.additionalInfo = {
                        activeUsers: Math.floor(Math.random() * (1000 - 100 + 1)) + 100,
                    };
                });
                setRelatedGames(list);
            }
        };
        if (gameData) {
            getRelatedGames();
        }
    }, [gameData.providerId]);

    const initData = () => {
        setGameData({gameId: 0, gameInlineType: 0, gameName: "", providerName: "", providerId: 0});
        setRelatedGames([]);
        setIsDemoMode(false);
        setGameUrl("");
    };

    useEffect(() => {
        initData();
    }, [pathname]);

    useEffect(() => {
        setGameUrl("");
    }, [userCurrency]);

    useEffect(() => {
        if (!gameData.hasDemo && hasGetGameUrl) {
            playHandler(false);
        }
    }, [userCurrency]);

    const onCollectClick = async () => {
        setIsCollect((b) => !b);
        if (isCollect) {
            await apiCasino.removeCollect(gameData.gameId, gameData.langId || 0);
        } else {
            await apiCasino.addCollect(gameData.gameId, gameData.langId || 0);
        }
    };

    useEffect(() => {
        const getCollectStatus = async () => {
            const {status, data} = await apiCasino.getCollectStatus(gameData.gameId, gameData.langId || 0);
            if (status === 200 && data.code === 0) {
                setIsCollect(data.data);
            }
        };
        if (isLogin && gameData.gameId) {
            getCollectStatus();
        }
    }, [isLogin, gameData]);

    // Add an effect to send the auth token when the iframe URL changes
    useEffect(() => {
        if (gameUrl && token) {
            // Give a short delay to make sure iframe is loaded before attempting to send token
            const timer = setTimeout(() => {
                sendAuthTokenToIframe();
            }, 1000);

            return () => clearTimeout(timer);
        }
    }, [gameUrl, token]);

    return {
        isMobile,
        isTablet,
        gameData,
        isDemoMode,
        isCollect,
        setIsDemoMode,
        onMaxClick,
        onCollectClick,
        currencyOpt: CURRENCY_OPTIONS,
        selectedId,
        onSelectChange,
        playHandler,
        showGameBanner,
        gameUrl,
        iframeRef,
        setIsLoadingGame,
        isLoadingGame,
        relatedGames,
        show,
        setShowGameBanner,
        gameIframeRef,
        sendAuthTokenToIframe,
        isUserPlayRealGame,
        isWidgetGame: isWidgetGame(),
    };
};
